import {
  BodyLong,
  Content,
  FlexDiv,
  Heading,
  NavBar,
  PileDiv,
  VerticalSpace
} from '@elhub/ds-components'
import styled from 'styled-components'

import TopContainer from 'components/TopContainer/TopContainer'

import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Feedback from '../components/Feedback'
import { LabelMessages } from '../locales/messages/label'
import { EnergyData as EnergyDataApi, ConsumptionGroups, Meta, Parties, PriceAreas, GridAreas, Municipalities, MeteringPoints, ProductionGroups, Updates, DatasetUsageGuidelines } from './apis'
import tokens from '@elhub/ds-tokens/dist/tokens.json'

const ApiContent = styled(Content)`
  @media (min-width: 1200px) {
    width: 1200px;
  }
`

const EnergyDataAPIs = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const index = window.location.href.indexOf('#')
  const location = index === -1 ? '' : window.location.href.substring(index)

  const getDescription = (): ReactNode => {
    switch (location) {
      case '#/meta':
        return <Meta />
      case '#/parties':
        return <Parties />
      case '#/price-areas':
        return <PriceAreas />
      case '#/grid-areas':
        return <GridAreas />
      case '#/municipalities':
        return <Municipalities />
      case '#/consumption-groups':
        return <ConsumptionGroups />
      case '#/metering-points':
        return <MeteringPoints />
      case '#/production-groups':
        return <ProductionGroups />
      case '#/updates':
        return <Updates />
      case '#/dataset-guidelines':
        return <DatasetUsageGuidelines />
      /* case '#/confidentiality-principles':
        return <ConfidentialityPrinciples /> */
      default:
        return (
          <>
            <Heading>Overview</Heading>
            <VerticalSpace />
            <BodyLong size='small'>
              <b>The Energy Data API provides access to datasets for various entities within ELHUB, including:</b>
              <VerticalSpace />
              <ul>
                <li>Price Areas</li>
                <li>Grid Areas</li>
                <li>Metering Points</li>
                <li>Municipalities</li>
              </ul>
              <VerticalSpace />
              <b>The API allows retrieving different datasets for each entity using the following format:</b>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: tokens['eds-color-grey-lighter90'], padding: '1rem' }}>
                <code>https://api.elhub.no/energy-data/v0/{'{entity}'}?dataset={'{dataset}'}</code>
              </pre>
              <b>Understanding the URL Parameters</b>
              <ul>
                <li>
                  {'{entity}'} – Represents the Elhub entities mentioned above (e.g., grid-areas, price-areas, metering-points, etc.)
                </li>
                <li>
                  {'{dataset}'} – Specifies the type of data to retrieve for the given entity, which can include:
                  <ul>
                    <li>
                      Consumption – Aggregated consumption data per entity (e.g. CONSUMPTION_PER_GROUP_MBA_HOUR)
                    </li>
                    <li>
                      Production – Aggregated production data per entity (e.g. PRODUCTION_PER_GROUP_MBA_HOUR)
                    </li>
                  </ul>
                </li>
              </ul>
              <VerticalSpace />
              For detailed information about each endpoint, click on the respective entity on the left navagation menu.
            </BodyLong>
            <VerticalSpace />
          </>
        )
    }
  }

  const handleNavigation = (link: string) => {
    navigate(link)
  }

  const navbarActive = ['dataset-guidelines', /* 'confidentiality-principles', */ 'price-areas', 'grid-areas', 'municipalities', 'metering-points', 'parties', 'consumption-groups', 'production-groups', 'meta', 'updates'].indexOf(window.location.href.split('#/')[1])

  return (
    <TopContainer>
      <ApiContent>
        <VerticalSpace size='2' />
        <Heading size='large' spacing>
          {t(LabelMessages.ENERGY_DATA_API as any)}
        </Heading>
        <VerticalSpace size='2' />
        <FlexDiv>
          <FlexDiv style={{ marginRight: '3rem' }}>
            <NavBar orientation='vertical' underscore size='small' defaultValue={navbarActive + 1}>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('/energy-data-api')
                }}
              >
                Overview
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/dataset-guidelines')
                }}
              >
                Usage Guidelines
              </NavBar.Link>
              {/* <NavBar.Link
                onClick={() => {
                  handleNavigation('#/confidentiality-principles')
                }}
              >
                Confidentiality Principles
              </NavBar.Link> */}
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/price-areas')
                }}
              >
                Price areas
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/grid-areas')
                }}
              >
                Grid areas
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/municipalities')
                }}
              >
                Municipalities
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/metering-points')
                }}
              >
                Metering points
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/parties')
                }}
              >
                Parties
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/consumption-groups')
                }}
              >
                Consumption groups
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/production-groups')
                }}
              >
                Production groups
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/meta')
                }}
              >
                Meta
              </NavBar.Link>
              <NavBar.Link
                onClick={() => {
                  handleNavigation('#/updates')
                }}
              >
                Updates
              </NavBar.Link>
            </NavBar>
          </FlexDiv>
          <PileDiv style={{ flex: 2 }}>
            <EnergyDataApi>{getDescription()}</EnergyDataApi>
            <VerticalSpace size='2' />
            <Feedback location='EnergyData API Overview page' />
          </PileDiv>
        </FlexDiv>
      </ApiContent>
    </TopContainer>
  )
}
export default EnergyDataAPIs
